import { resolveVariant, getAvailableValues, currencySymbol, fetchFullProduct } from './variantUtils.js';

/**
 * VariationDesktopPicker — inline variation picker for desktop.
 *
 * Replaces the product list in place (product list is hidden, picker shown).
 * Works in both Shadow DOM and regular document; avoids position:absolute
 * which fails when picker is in document (inset:0 would be relative to wrong ancestor).
 *
 * Usage:
 *   const picker = new VariationDesktopPicker({ primaryColor, onProductAdd, onBack });
 *   pickerRelativeEl.appendChild(picker.render(product));
 *
 * picker.destroy() removes it and calls onBack().
 */
export class VariationDesktopPicker {
  constructor(options = {}) {
    this.onProductAdd = options.onProductAdd || (() => {});
    this.onBack = options.onBack || (() => {});
    this.primaryColor = options.primaryColor || '#7c3aed';
    this.element = null;
    this.product = null;
    this.selectedValues = [];
  }

  render(product) {
    this.product = product;
    this.element = document.createElement('div');
    this.element.className = 'ttp-var-picker';
    this.element.style.setProperty('--ttp-var-primary', this.primaryColor);

    if (!product.variants) {
      // Show loading state, fetch variants, then re-render
      this.element.innerHTML = `
        <div class="ttp-var-header">
          <button class="ttp-var-back-btn" id="ttpVarBack">←</button>
          <span class="ttp-var-title">${product.name}</span>
        </div>
        <div class="ttp-var-body" style="align-items:center;justify-content:center;display:flex;flex-direction:column;gap:10px;">
          <div class="ttp-var-spinner"></div>
          <div style="font-size:12px;color:#64748b;">Loading options...</div>
        </div>
      `;

      this.element.querySelector('#ttpVarBack').addEventListener('click', () => {
        this.destroy();
        this.onBack();
      });

      fetchFullProduct(product).then(fullProduct => {
        if (!fullProduct || !fullProduct.variants) {
          this.destroy();
          this.onBack();
          return;
        }
        // Re-render in place with full data
        this.product = fullProduct;
        this.selectedValues = new Array(fullProduct.options.length).fill(null);
        fullProduct.options.forEach((opt, i) => {
          const available = getAvailableValues(fullProduct.variants, i, this.selectedValues);
          if (available.length > 0) this.selectedValues[i] = available[0];
        });
        this.element.innerHTML = this._buildHTML();
        this._attachHandlers();
        this._updateAddButton();
      });

      return this.element;
    }

    // Normal flow — variants already loaded
    this.selectedValues = new Array(product.options.length).fill(null);
    product.options.forEach((opt, i) => {
      const available = getAvailableValues(product.variants, i, this.selectedValues);
      if (available.length > 0) this.selectedValues[i] = available[0];
    });

    this.element.innerHTML = this._buildHTML();
    this._attachHandlers();
    this._updateAddButton();
    return this.element;
  }

  destroy() {
    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
    this.element = null;
    this.product = null;
  }

  _buildHTML() {
    const p = this.product;
    const sym = currencySymbol(p.currency);
    const resolvedVariant = resolveVariant(p.variants, this.selectedValues);
    const currentPrice = resolvedVariant ? resolvedVariant.price : p.price;
    const imgUrl = resolvedVariant?.imageUrl || p.imageUrl || '';
    const hasPageUrl = p.productPageUrl && p.productPageUrl.trim() !== '';

    return `
      <div class="ttp-var-header">
        <button class="ttp-var-back-btn" id="ttpVarBack">←</button>
        <span class="ttp-var-title">${p.name}</span>
        ${hasPageUrl
          ? `<a class="ttp-var-page-link" href="${p.productPageUrl}">View page ↗</a>`
          : ''}
      </div>

      <div class="ttp-var-body">
        <div class="ttp-var-preview">
          <div class="ttp-var-preview-img" id="ttpVarPreviewImg">
            ${imgUrl
              ? `<img src="${imgUrl}" alt="${p.name}" onerror="this.style.display='none'"/>`
              : ''}
          </div>
          <div class="ttp-var-preview-info">
            ${p.brand ? `<div class="ttp-var-preview-brand">${p.brand}</div>` : ''}
            <div class="ttp-var-preview-name">${p.name}</div>
            <div class="ttp-var-preview-price" id="ttpVarPreviewPrice">${sym}${currentPrice.toFixed(2)}</div>
            <div class="ttp-var-preview-selected" id="ttpVarPreviewSel">Select options</div>
          </div>
        </div>

        <div class="ttp-var-options" id="ttpVarOptions">
          ${this._renderOptions()}
        </div>
      </div>

      <div class="ttp-var-footer">
        <button class="ttp-var-add-btn" id="ttpVarAddBtn" disabled>
          Add to Cart — ${sym}${currentPrice.toFixed(2)}
        </button>
      </div>
    `;
  }

  _renderOptions() {
    return this.product.options.map((opt, optIdx) => {
      const availableValues = getAvailableValues(
        this.product.variants, optIdx, this.selectedValues
      );
      const isColor = opt.name.toLowerCase() === 'color' ||
                      opt.name.toLowerCase() === 'colour';

      if (isColor) {
        return `
          <div class="ttp-var-section">
            <div class="ttp-var-section-label">${opt.name}</div>
            <div class="ttp-var-swatches">
              ${opt.values.map(val => {
                const oos = !availableValues.includes(val);
                const selected = this.selectedValues[optIdx] === val;
                return `<div
                  class="ttp-var-swatch${selected ? ' selected' : ''}${oos ? ' oos' : ''}"
                  data-value="${val}"
                  data-opt-idx="${optIdx}"
                  style="background:${this._colorNameToHex(val)};"
                  title="${val}${oos ? ' (unavailable)' : ''}"
                ></div>`;
              }).join('')}
            </div>
          </div>`;
      }

      return `
        <div class="ttp-var-section">
          <div class="ttp-var-section-label">${opt.name}</div>
          <div class="ttp-var-pills">
            ${opt.values.map(val => {
              const oos = !availableValues.includes(val);
              const selected = this.selectedValues[optIdx] === val;
              return `<button
                class="ttp-var-pill${selected ? ' selected' : ''}${oos ? ' oos' : ''}"
                data-value="${val}"
                data-opt-idx="${optIdx}"
                ${oos ? 'disabled' : ''}
              >${val}</button>`;
            }).join('')}
          </div>
        </div>`;
    }).join('');
  }

  _attachHandlers() {
    this.element.querySelector('#ttpVarBack').addEventListener('click', () => {
      this.destroy();
      this.onBack();
    });

    this.element.querySelector('#ttpVarAddBtn').addEventListener('click', () => {
      this._handleAdd();
    });

    this._attachOptionHandlers();
  }

  _attachOptionHandlers() {
    this.element.querySelectorAll('.ttp-var-swatch:not(.oos)').forEach(el => {
      el.addEventListener('click', () =>
        this._selectOption(parseInt(el.dataset.optIdx), el.dataset.value)
      );
    });
    this.element.querySelectorAll('.ttp-var-pill:not(.oos)').forEach(el => {
      el.addEventListener('click', () =>
        this._selectOption(parseInt(el.dataset.optIdx), el.dataset.value)
      );
    });
  }

  _selectOption(optIdx, value) {
    this.selectedValues[optIdx] = value;
    this.element.querySelector('#ttpVarOptions').innerHTML = this._renderOptions();
    this._attachOptionHandlers();
    this._updateAddButton();
    this._updatePreview();
  }

  _updateAddButton() {
    const sym = currencySymbol(this.product.currency);
    const variant = resolveVariant(this.product.variants, this.selectedValues);
    const btn = this.element.querySelector('#ttpVarAddBtn');
    const allSelected = this.selectedValues.every(v => v !== null);

    if (!allSelected) {
      btn.textContent = 'Select all options';
      btn.disabled = true;
      return;
    }
    if (!variant || !variant.available) {
      btn.textContent = 'Unavailable';
      btn.disabled = true;
      return;
    }
    const price = variant.price ?? this.product.price;
    btn.textContent = `Add to Cart — ${sym}${price.toFixed(2)}`;
    btn.disabled = false;
  }

  _updatePreview() {
    const sym = currencySymbol(this.product.currency);
    const variant = resolveVariant(this.product.variants, this.selectedValues);
    const imgUrl = variant?.imageUrl || this.product.imageUrl || '';

    // Update image
    const imgEl = this.element.querySelector('#ttpVarPreviewImg');
    if (imgEl && imgUrl) {
      imgEl.innerHTML = `<img src="${imgUrl}" alt="${this.product.name}"
        onerror="this.style.display='none'"/>`;
    }

    // Update price
    const priceEl = this.element.querySelector('#ttpVarPreviewPrice');
    if (priceEl && variant) {
      priceEl.textContent = `${sym}${variant.price.toFixed(2)}`;
    }

    // Update selected summary
    const selEl = this.element.querySelector('#ttpVarPreviewSel');
    if (selEl) {
      const filled = this.selectedValues.filter(v => v !== null);
      selEl.textContent = filled.length > 0
        ? filled.join(' · ')
        : 'Select options';
    }
  }

  _handleAdd() {
    const variant = resolveVariant(this.product.variants, this.selectedValues);
    if (!variant || !variant.available) return;

    const btn = this.element.querySelector('#ttpVarAddBtn');
    btn.textContent = '✓ Added!';
    btn.disabled = true;

    const productData = {
      ...this.product,
      id: variant.id,
      price: variant.price,
      selectedOptions: this.selectedValues,
      selectedAmount: 1,
      sellBy: 'quantity',
    };

    this.onProductAdd(this.element, productData);
    setTimeout(() => {
      this.destroy();
      this.onBack();
    }, 800);
  }

  _colorNameToHex(name) {
    const map = {
      black: '#1e293b', white: '#f8fafc', red: '#ef4444',
      blue: '#3b82f6', green: '#22c55e', yellow: '#eab308',
      orange: '#f97316', purple: '#a855f7', pink: '#ec4899',
      grey: '#94a3b8', gray: '#94a3b8', navy: '#1e3a5f',
      brown: '#92400e', beige: '#d4b483', cream: '#fef9c3',
    };
    return map[name.toLowerCase()] || '#94a3b8';
  }
}
